import { createContext, useEffect, useState } from "react";
import { setCookie, parseCookies, destroyCookie } from "nookies";

import api from "../services/api";
import {
  recoverUserInformation,
  signInRequest,
  totpConfirmRequest,
  totpVerifyRequest,
} from "../services/auth";
import { useHistory } from "react-router-dom";

export const AuthContext = createContext({});

// Session cookie expires every hour
const SESSION_COOKIE_MAX_AGE = 60 * 60;

// Partial TOTP JWT expires in 5 minutes (matches API)
const TOTP_COOKIE_MAX_AGE = 5 * 60;

// Validates sessions every minute
const VALIDATE_TIMEOUT = 60 * 1000;

const SESSION_COOKIE = "fundamentos.token";
const TOTP_COOKIE = "fundamentos.totpToken";

export function AuthProvider({ children }) {
  const history = useHistory();

  const [user, setUser] = useState();

  const isAuthenticated = !!user;

  const [loadingCurrentUser, setLoadingCurrentUser] = useState(true);

  useEffect(() => {
    const token = getSessionCookie();

    if (token && token !== "undefined") {
      setApiAuthorizationHeader(token);
      recoverUserInformation().then((response) => {
        setUser(response.user);
        setLoadingCurrentUser(false);
      });
    } else {
      setLoadingCurrentUser(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) validateSignedUser();
  });

  const getSessionCookie = () => {
    const { [SESSION_COOKIE]: token } = parseCookies();

    return token;
  };

  const getTotpCookie = () => {
    const { [TOTP_COOKIE]: token } = parseCookies();

    return token;
  };

  const setApiAuthorizationHeader = (token) => {
    api.defaults.headers["Authorization"] = `Bearer ${token}`;
  };

  const validateSignedUser = async () => {
    if (getSessionCookie()) setTimeout(validateSignedUser, VALIDATE_TIMEOUT);
    else signOut();
  };

  const establishFullSession = (token, sessionUser) => {
    destroyCookie(undefined, TOTP_COOKIE);
    setCookie(undefined, SESSION_COOKIE, token, {
      maxAge: SESSION_COOKIE_MAX_AGE,
    });
    setApiAuthorizationHeader(token);
    setUser(sessionUser);
    validateSignedUser();
  };

  const signIn = async ({ email, password }) => {
    const result = await signInRequest({
      email,
      password,
    });

    if (result.totpPending) {
      setCookie(undefined, TOTP_COOKIE, result.totpToken, {
        maxAge: TOTP_COOKIE_MAX_AGE,
      });
      return { totpPending: true, totpAction: result.totpAction };
    }

    const { token, user: sessionUser, errors } = result;

    if (token) {
      establishFullSession(token, sessionUser);
      return { totpPending: false };
    } else {
      throw new Error("Error loggin in", errors);
    }
  };

  const confirmTotpSetup = async (code) => {
    const totpToken = getTotpCookie();
    if (!totpToken) {
      throw new Error("Token TOTP não encontrado");
    }

    const { token, user: sessionUser } = await totpConfirmRequest(
      totpToken,
      code
    );
    establishFullSession(token, sessionUser);
  };

  const verifyTotp = async (code) => {
    const totpToken = getTotpCookie();
    if (!totpToken) {
      throw new Error("Token TOTP não encontrado");
    }

    const { token, user: sessionUser } = await totpVerifyRequest(
      totpToken,
      code
    );
    establishFullSession(token, sessionUser);
  };

  const signOut = () => {
    setLoadingCurrentUser(true);
    destroyCookie(undefined, SESSION_COOKIE);
    destroyCookie(undefined, TOTP_COOKIE);
    delete api.defaults.headers["Authorization"];

    setUser();
    history.push("/");
    setLoadingCurrentUser(false);
  };

  const userHasPermission = (permission) => {
    return user?.permissions?.includes(permission);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        history,
        isAuthenticated,
        signIn,
        signOut,
        confirmTotpSetup,
        verifyTotp,
        loadingCurrentUser,
        userHasPermission,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
